import React, { useState, useEffect } from 'react';
import { ChevronDownRegular, PersonRegular, LocationRegular, MailRegular, PhoneRegular, OpenRegular, DocumentRegular, ChatRegular, ArrowRightRegular, SendRegular, MailInboxRegular } from '@fluentui/react-icons';
import contactService from '../services/contactService';
import authService from '../services/authService';
import incidentCommunicationsService from '../services/incidentCommunicationsService';
import emailConnectionsService from '../services/emailConnectionsService';
import ContactCreator from './ContactCreator';
import { useLanguage } from '../contexts/LanguageContext';
import { useTextSize } from '../contexts/TextSizeContext';
import config from '../../config';

export default function ContactInfo({ isOpen, onToggle, onShowDetail, contextVersion }) {
  const [contactData, setContactData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [communications, setCommunications] = useState([]);
  const [loadingCommunications, setLoadingCommunications] = useState(false);
  const [communicationsError, setCommunicationsError] = useState(null);
  const [communicationsOpen, setCommunicationsOpen] = useState(false);
  const { t } = useLanguage();
  const { currentTextSize } = useTextSize();

  useEffect(() => {
    if (isOpen) {
      fetchContactData();
    }
  }, [isOpen, contextVersion]);

  useEffect(() => {
    if (contactData && communicationsOpen && communications.length === 0) {
      fetchCommunications();
    }
  }, [contactData, communicationsOpen]);

  const fetchContactData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Check if contact ID is available before fetching
      if (!contactService.getContactId()) {
        setContactData(null);
        setError(null); // Clear any previous error
        setCommunications([]); // Clear communications when contact changes
        setCommunicationsOpen(false); // Close communications section
        setLoading(false);
        return;
      }
      
      const response = await contactService.getContact();
      setContactData(response.data);
      // Reset communications when contact changes
      setCommunications([]);
      setCommunicationsError(null);
    } catch (err) {
      setError(err.message);
      console.error('Failed to fetch contact data:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchCommunications = async () => {
    setLoadingCommunications(true);
    setCommunicationsError(null);
    
    try {
      // Get contact ID
      const contactId = contactData?.contact_id || contactData?.id || contactService.getContactId();
      
      if (!contactId) {
        console.warn('No contact ID available for fetching communications');
        setCommunications([]);
        setLoadingCommunications(false);
        return;
      }

      // Fetch user's email connection IDs
      const connectionIds = await emailConnectionsService.getConnectionIds();
      
      if (connectionIds.length === 0) {
        console.warn('No email connections found for user');
        setCommunications([]);
        setLoadingCommunications(false);
        return;
      }

      // Fetch latest 5 communications for this contact
      const result = await incidentCommunicationsService.getLatestThreadsFull({
        contactId,
        connectionIds,
        pageSize: 5,
        page: 1
      });

      if (result.success) {
        setCommunications(result.data || []);
      } else {
        throw new Error('Failed to fetch communications');
      }
    } catch (err) {
      console.error('Error fetching communications:', err);
      setCommunicationsError(err.message);
    } finally {
      setLoadingCommunications(false);
    }
  };

  const handleOpenInDoNexus = () => {
    console.log('Contact data:', contactData); // Debug log
    
    // Try multiple possible field names for the contact ID
    const contactId = contactData?.contact_id || contactData?.id || contactService.getContactId();
    
    if (!contactId) {
      console.error('No contact ID available. Contact data:', contactData);
      return;
    }

    console.log('Opening contact with ID:', contactId); // Debug log
    
    const contactUrl = `${config.APP_BASE_URL}/stammdaten/person/${contactId}`;
    
    // Get access token to pass to the main app for SSO
    const accessToken = authService.getAccessToken();
    
    if (accessToken) {
      // Open in new window with token parameter for SSO
      // The main app should recognize this token and authenticate the user automatically
      const urlWithAuth = `${contactUrl}?addin_token=${encodeURIComponent(accessToken)}`;
      console.log('Opening URL with auth:', urlWithAuth); // Debug log
      window.open(urlWithAuth, '_blank');
    } else {
      // Open without token if not available
      console.log('Opening URL without auth:', contactUrl); // Debug log
      window.open(contactUrl, '_blank');
    }
  };

  const handleViewAllCommunications = () => {
    const contactId = contactData?.contact_id || contactData?.id || contactService.getContactId();
    
    if (!contactId) {
      console.error('No contact ID available');
      return;
    }

    const communicationsUrl = `${config.APP_BASE_URL}/communications?contact_id=${contactId}`;
    const accessToken = authService.getAccessToken();
    
    if (accessToken) {
      const urlWithAuth = `${communicationsUrl}&addin_token=${encodeURIComponent(accessToken)}`;
      window.open(urlWithAuth, '_blank');
    } else {
      window.open(communicationsUrl, '_blank');
    }
  };

  const handleContactCreated = (newContact) => {
    setContactData(newContact);
    // Reset communications
    setCommunications([]);
    setCommunicationsError(null);
    setCommunicationsOpen(false);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString(undefined, { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="border-b border-gray-300">
      <button 
        className={`flex items-center justify-between w-full ${currentTextSize.cardPadding} hover:bg-gray-50 transition-colors`}
        onClick={onToggle}
      >
        <div className="flex items-center gap-2">
          <PersonRegular className="h-4 w-4 text-gray-600" />
          <h3 className={`${currentTextSize.headerText} font-semibold text-gray-900`}>{t('contactInfo.title')}</h3>
        </div>
        <ChevronDownRegular
          className={`h-4 w-4 text-gray-500 transition-transform ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </button>

      {isOpen && (
        <div className={`px-4 pb-4 ${currentTextSize.spacing}`}>
          {loading && (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
              <span className={`ml-2 ${currentTextSize.bodyText} text-gray-600`}>{t('contactInfo.loadingContactData')}</span>
            </div>
          )}

          {!loading && !contactData && !error && (
            <ContactCreator 
              onContactCreated={handleContactCreated}
            />
          )}

          {error && (
            <div className={`bg-red-50 border border-red-200 rounded-lg ${currentTextSize.padding}`}>
              <div className="flex items-center">
                <div className={`text-red-600 ${currentTextSize.bodyText}`}>
                  <strong>{t('contactInfo.errorLoadingContact')}</strong> {error}
                </div>
              </div>
              <button 
                onClick={fetchContactData}
                className={`mt-2 ${currentTextSize.bodyText} text-red-600 hover:text-red-700 underline`}
              >
                {t('contactInfo.tryAgain')}
              </button>
            </div>
          )}

          {contactData && !loading && !error && (
            <>
              <div className="flex items-center gap-3">
                <div 
                  className={`${currentTextSize.avatarSize === 'h-5 w-5' ? 'h-10 w-10' : currentTextSize.avatarSize === 'h-6 w-6' ? 'h-11 w-11' : 'h-12 w-12'} bg-blue-600 text-white rounded-full flex items-center justify-center font-semibold cursor-pointer hover:opacity-80 transition-opacity`}
                  onClick={() => onShowDetail?.(contactData)}
                >
                  {contactData.name ? contactData.name.charAt(0).toUpperCase() : 'C'}
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className={`${currentTextSize.headerText} font-semibold text-gray-900`}>
                    {contactData.name} {contactData.surname}
                  </h4>
                </div>
              </div>

              <div className={currentTextSize.spacing}>
                <div className={`flex items-center gap-2 ${currentTextSize.bodyText}`}>
                  <LocationRegular className="h-4 w-4 text-gray-500" />
                  <span className="text-gray-900">
                    {contactData.street_name} {contactData.house_number}, {contactData.zip_code} {contactData.city}
                  </span>
                </div>
                <div className={`flex items-center gap-2 ${currentTextSize.bodyText}`}>
                  <MailRegular className="h-4 w-4 text-gray-500" />
                  <span className="text-gray-900">{contactData.email}</span>
                </div>
                <div className={`flex items-center gap-2 ${currentTextSize.bodyText}`}>
                  <PhoneRegular className="h-4 w-4 text-gray-500" />
                  <span className="text-gray-900">{contactData.phone}</span>
                </div>
              </div>

              <div className="space-y-1">
                <button 
                  onClick={handleOpenInDoNexus}
                  className={`w-full flex items-center text-blue-600 hover:text-blue-700 hover:bg-blue-50 ${currentTextSize.padding} rounded ${currentTextSize.bodyText} font-medium`}
                >
                  <OpenRegular className="h-4 w-4 mr-2" />
                  {t('contactInfo.openContactInDoNexus')}
                </button>
              </div>

              {/* Recent Communications Section */}
              <div className="mt-6 pt-4 border-t border-gray-200">
                <button
                  onClick={() => setCommunicationsOpen(!communicationsOpen)}
                  className="w-full flex items-center justify-between hover:bg-gray-50 p-2 -mx-2 rounded transition-colors"
                >
                  <h4 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                    <ChatRegular className="h-4 w-4 text-gray-600" />
                    {t('contactInfo.recentCommunications')}
                  </h4>
                  <ChevronDownRegular
                    className={`h-4 w-4 text-gray-500 transition-transform ${
                      communicationsOpen ? "rotate-180" : ""
                    }`}
                  />
                </button>

                {communicationsOpen && (
                  <>
                    {loadingCommunications && (
                      <div className="flex items-center justify-center py-4">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                        <span className="ml-2 text-xs text-gray-600">{t('contactInfo.loadingCommunications')}</span>
                      </div>
                    )}

                    {communicationsError && !loadingCommunications && (
                      <div className="bg-red-50 border border-red-200 rounded-lg p-3 mt-3">
                        <div className="text-red-600 text-xs">
                          <strong>{t('contactInfo.errorLoadingCommunications')}</strong> {communicationsError}
                        </div>
                        <button 
                          onClick={fetchCommunications}
                          className="mt-2 text-xs text-red-600 hover:text-red-700 underline"
                        >
                          {t('contactInfo.tryAgain')}
                        </button>
                      </div>
                    )}

                    {!loadingCommunications && !communicationsError && communications.length === 0 && (
                      <div className="text-xs text-gray-500 text-center py-4">
                        {t('contactInfo.noCommunications')}
                      </div>
                    )}

                    {!loadingCommunications && !communicationsError && communications.length > 0 && (
                      <div className="space-y-3 mt-3">
                        {communications.map((thread) => {
                          const comm = thread.latest || thread;
                          const threadCount = thread.thread_count || comm.thread_count || 1;
                          const direction = comm.direction === 'inbound' ? 'received' : comm.direction === 'outbound' ? 'sent' : comm.direction;
                          const threadUrl = `${config.APP_BASE_URL}/communications?thread_id=${thread.thread_id || comm.thread_id}`;
                          const accessToken = authService.getAccessToken();
                          const fullUrl = accessToken 
                            ? `${threadUrl}&addin_token=${encodeURIComponent(accessToken)}`
                            : threadUrl;
                          
                          return (
                            <div 
                              key={comm.id || thread.thread_id} 
                              className="pb-3 border-b border-gray-200 last:border-b-0 last:pb-0"
                            >
                              <div className="flex items-start gap-2 mb-1">
                                {direction === 'sent' || direction === 'outbound' ? (
                                  <SendRegular className="h-3 w-3 text-gray-400 flex-shrink-0 mt-0.5" />
                                ) : (
                                  <MailInboxRegular className="h-3 w-3 text-gray-400 flex-shrink-0 mt-0.5" />
                                )}
                                <div className="flex-1 min-w-0">
                                  <a 
                                    href={fullUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-xs font-medium text-blue-600 hover:text-blue-800 underline truncate block"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    {comm.subject_line || t('contactInfo.noSubject')}
                                  </a>
                                </div>
                              </div>
                              
                              {comm.preview && (
                                <p className="text-xs text-gray-600 mb-2 ml-5 line-clamp-2">
                                  {comm.preview}
                                </p>
                              )}
                              
                              <div className="flex items-center justify-between text-xs text-gray-500 ml-5">
                                <span>{formatDate(comm.occurred_at || comm.created_at)}</span>
                                {threadCount > 1 && (
                                  <span className="flex items-center gap-1">
                                    <ChatRegular className="h-3 w-3" />
                                    {threadCount}
                                  </span>
                                )}
                              </div>
                            </div>
                          );
                        })}

                        <button 
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewAllCommunications();
                          }}
                          className="w-full flex items-center justify-center text-blue-600 hover:text-blue-700 hover:bg-blue-50 p-2 rounded text-xs font-medium mt-2"
                        >
                          {t('contactInfo.viewAllCommunications')}
                          <ArrowRightRegular className="h-3 w-3 ml-1" />
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
